import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostBinding,
  OnChanges,
  OnDestroy,
  OnInit,
  Renderer2,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { LineChartComponent } from '@shared-lib/chart-components/line-chart/line-chart.component';
import { Subscription } from 'rxjs';
import { KeycloakService } from 'keycloak-angular';
import { QuadBubbleLegendComponent as ParentQuadBubbleLegendComponent } from '@shared-lib/global-components/quad-bubble-legend/quad-bubble-legend.component';
import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';
import { ChartWrapperInstance, ChartWrapperService } from '@shared-lib/shared-services/chart-wrapper.service';
import { environment } from '../../../projects/my-app/src/environments/environment';

interface SummaryMetric {
  label: string;
  value: string;
  sourceLabel: string;
  requestCardValue?: string;
  groupTitle?: string;
  hot?: boolean;
  dividerAfter?: boolean;
}

interface SummaryMetricConfig {
  label: string;
  labelByGroup?: Record<string, string>;
  fieldsByGroup?: Record<string, string[]>;
  requestCardValueByGroup?: Record<string, string>;
  requestCardValue?: string;
  fields?: string[];
  field?: string;
  aggregation?: 'avg' | 'sum' | 'first';
  format?: 'percent' | 'currency' | 'number' | 'text';
  hot?: boolean;
  dividerAfter?: boolean;
  visibleGroups?: string[];
}

interface SummarySortConfig {
  metricLabel?: string;
  field?: string;
  direction?: 'asc' | 'desc';
  nulls?: 'first' | 'last';
}

interface SummaryCard {
  title: string;
  metrics: SummaryMetric[];
}

@Component({
  selector: 'app-quad-bubble-legend',
  standalone: true,
  imports: [CommonModule, LineChartComponent],
  templateUrl: './quad-bubble-legend.component.html',
  styleUrls: ['./quad-bubble-legend.component.scss'],
})
export class QuadBubbleLegendComponent extends ParentQuadBubbleLegendComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  private static readonly BEARER_TOKEN_PLACEHOLDER = '__BEARER_TOKEN__';
  summaryCards: SummaryCard[] = [];
  isSummaryLoading = false;
  activeHoverMetric: SummaryMetric | null = null;
  activeHoverConfig: any = null;
  activeHoverStyle: Record<string, string> | null = null;
  hoverChartLoading = false;
  hoverChartError = '';

  private readonly hostElement = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly summaryHttp = inject(HttpClient);
  private readonly keycloakService = inject(KeycloakService);
  private readonly summaryGlobalFilterService = inject(GlobalFilterService);
  private readonly summaryWrapperService = inject(ChartWrapperService);
  private readonly summaryWrapper: ChartWrapperInstance = this.summaryWrapperService.createInstance();
  @ViewChild('hoverCard') private hoverCard?: ElementRef<HTMLElement>;
  private summaryDataSub?: Subscription;
  private hoverDataSub?: Subscription;
  private summaryLastLoadSignature = '';
  private summaryWrapperAttached = false;
  private syncedSummaryHeightElements: HTMLElement[] = [];
  private hideHoverTimer?: ReturnType<typeof setTimeout>;
  private activeHoverAnchorRect: { left: number; right: number; top: number; width: number; height: number } | null = null;

  @HostBinding('class.summary-panel-host')
  get isSummaryPanelHost(): boolean {
    return this.isSummaryPanel;
  }

  @HostBinding('class.has-explicit-height-host')
  get hasExplicitHeightHost(): boolean {
    return !!this.resolvedHeight;
  }

  override ngOnInit(): void {
    if (!this.isSummaryPanel) {
      super.ngOnInit();
      return;
    }

    this.initializeSummaryLoader();
    this.syncSummaryContainerHeight();
  }

  override ngOnChanges(changes: SimpleChanges): void {
    if (!this.isSummaryPanel) {
      super.ngOnChanges(changes);
      return;
    }

    if (changes['data']) {
      this.summaryWrapper.updateData(this.data);
      this.initializeSummaryLoader();
    }

    this.syncSummaryContainerHeight();
  }

  ngAfterViewInit(): void {
    this.syncSummaryContainerHeight();
  }

  override ngOnDestroy(): void {
    this.summaryDataSub?.unsubscribe();
    this.hoverDataSub?.unsubscribe();
    this.summaryWrapper.detach();
    this.summaryWrapperAttached = false;
    this.clearSyncedSummaryContainerHeight();
    this.hideHoverCard();

    if (!this.isSummaryPanel) {
      super.ngOnDestroy();
    }
  }

  get isSummaryPanel(): boolean {
    const mode = String(this.data?.mode || '').toLowerCase();
    return mode === 'summary-api' || mode === 'deepdive-hardcoded';
  }

  get markerShapeClass(): string {
    const markerShape = String(this.data?.markerShape || this.data?.swatchShape || '')
      .trim()
      .toLowerCase();

    return markerShape === 'square' ? 'swatch-square' : '';
  }

  get summaryCardWidth(): string | null {
    return this.toCssSize(this.data?.summaryCardWidth ?? this.data?.cardWidth);
  }

  get summaryCardMinWidth(): string | null {
    return this.toCssSize(this.data?.summaryCardMinWidth ?? this.data?.cardMinWidth);
  }

  get resolvedHeight(): string | null {
    return this.toCssSize(this.data?.height ?? this.data?.cardHeight);
  }

  get hasHoverCard(): boolean {
    return !!this.activeHoverConfig;
  }

  get hoverPopupsEnabled(): boolean {
    return this.data?.hoverPopupsEnabled !== false;
  }

  // "notesByGroup" is an optional per-hoverChart JSON config (only set on PPI today) mapping a
  // card's group title (e.g. "Military", "Cons") to a { name, id } detail shown below the chart.
  // Other hover charts without notesByGroup configured simply render nothing extra.
  get activeHoverNote(): { name: string; id: string } | null {
    const notesByGroup = this.activeHoverConfig?.notesByGroup;
    if (!notesByGroup || typeof notesByGroup !== 'object') {
      return null;
    }

    const groupTitle = this.activeHoverMetric?.groupTitle;
    if (!groupTitle) {
      return null;
    }

    const normalizedGroupTitle = this.normalizeApplicationTitle(groupTitle).toLowerCase();
    const match = Object.entries(notesByGroup).find(([key]) => (
      this.normalizeApplicationTitle(key).toLowerCase() === normalizedGroupTitle
    ));

    if (!match) {
      return null;
    }

    const note = match[1] as any;
    const name = String(note?.name ?? '').trim();
    const id = String(note?.id ?? '').trim();
    return name || id ? { name, id } : null;
  }

  showMetricHover(metric: SummaryMetric, event: MouseEvent): void {
    if (!this.hoverPopupsEnabled) {
      this.hideHoverCard();
      return;
    }

    const chartConfig = this.getMetricHoverConfig(metric);
    if (!chartConfig) {
      this.hideHoverCard();
      return;
    }

    this.clearHideHoverTimer();
    this.activeHoverMetric = metric;
    this.hoverChartLoading = true;
    this.hoverChartError = '';
    this.activeHoverAnchorRect = this.getHoverAnchorRect(event);
    this.activeHoverStyle = this.buildHoverStyle(chartConfig, this.activeHoverAnchorRect);
    this.activeHoverConfig = {
      ...chartConfig,
      type: 'line-chart',
      widgetId: chartConfig.widgetId || `${this.data?.widgetId || 'quad-bubble-legend'}-${this.normalizeHoverKey(metric.sourceLabel)}-hover-chart`,
      apiUrl: '',
      data: { category: [], value: [] },
    };

    this.loadMetricHoverChart(metric, chartConfig);
  }

  scheduleHideHoverCard(): void {
    this.clearHideHoverTimer();
    this.hideHoverTimer = setTimeout(() => this.hideHoverCard(), 120);
  }

  keepHoverCardOpen(): void {
    this.clearHideHoverTimer();
  }

  private syncSummaryContainerHeight(): void {
    this.clearSyncedSummaryContainerHeight();

    const height = this.resolvedHeight;
    if (!this.isSummaryPanel || !height || this.data?.syncContainerHeight === false) {
      return;
    }

    queueMicrotask(() => {
      const host = this.hostElement.nativeElement;
      const elements = [
        host.closest('.card-body'),
        host.closest('.card'),
        host.closest('app-widget-renderer'),
        host.closest('.grid-widget'),
      ].filter((element): element is HTMLElement => !!element);

      const uniqueElements = Array.from(new Set(elements));
      uniqueElements.forEach((element) => {
        this.renderer.setStyle(element, 'height', height);
        this.renderer.setStyle(element, 'min-height', height);
        this.renderer.setStyle(element, 'max-height', height);
      });
      this.syncedSummaryHeightElements = uniqueElements;
    });
  }

  private clearSyncedSummaryContainerHeight(): void {
    this.syncedSummaryHeightElements.forEach((element) => {
      this.renderer.removeStyle(element, 'height');
      this.renderer.removeStyle(element, 'min-height');
      this.renderer.removeStyle(element, 'max-height');
    });
    this.syncedSummaryHeightElements = [];
  }

  private loadSummaryCards(filters: any = {}): void {
    if (!this.data?.apiUrl) {
      this.summaryCards = [];
      return;
    }

    const loadSignature = this.getSummaryLoadSignature(filters);
    if (filters?.widgetId && loadSignature === this.summaryLastLoadSignature) {
      return;
    }
    this.summaryLastLoadSignature = loadSignature;

    this.summaryDataSub?.unsubscribe();
    this.isSummaryLoading = true;
    const request$ = String(this.data.method || 'post').toLowerCase() === 'get'
      ? this.summaryHttp.get(this.resolveSummaryUrl(this.data.apiUrl), {
        headers: this.resolveSummaryHeaders(filters),
      })
      : this.summaryHttp.post(
        this.resolveSummaryUrl(this.data.apiUrl),
        this.buildSummaryPayload(filters),
        { headers: this.resolveSummaryHeaders(filters) },
      );

    this.summaryDataSub = request$
      .subscribe({
        next: (response) => {
          this.summaryCards = this.buildSummaryCards(this.resolveSummaryRows(response));
          this.isSummaryLoading = false;
        },
        error: () => {
          this.summaryCards = [];
          this.isSummaryLoading = false;
        },
      });
  }

  private buildSummaryPayload(filters: any): Record<string, any> {
    return {
      requestData: this.buildSummaryRequestData(filters),
      responseList: [],
      entity: this.data?.entity || 'default_entity',
      workflowId: this.data?.workflowId || 'default_workflow',
      endpoints: [
        {
          functionName: this.data?.functionName || 'Account_classification',
          version: this.data?.version || '1.0',
        },
      ],
      communicationType: 'rest',
      version: this.data?.version || '1.0',
    };
  }

  private buildSummaryRequestData(filters: any): Record<string, any> {
    const effectiveFilters = this.resolveEffectiveFilters(filters);
    const requestParams = {
      ...(effectiveFilters?.navigationRequestParams || {}),
      ...(effectiveFilters?.requestParams || {}),
    };
    const queryParams = effectiveFilters?.queryParams || {};
    const requestData: Record<string, any> = {
      ...(this.data?.defaultRequestData || {}),
      ...(this.data?.customPayload || {}),
    };
    const payloadKeys = this.getStringList(
      this.data?.requestPayloadKeys || this.data?.requestFilterKeys,
      ['product_price_type'],
    );
    const keyMap = this.data?.requestKeyMap || {};

    payloadKeys.forEach((key) => {
      const requestKey = keyMap?.[key] || key;
      const value = requestParams[key] ?? queryParams[key] ?? requestParams[requestKey] ?? queryParams[requestKey];
      if (value !== undefined && value !== null && value !== '') {
        requestData[requestKey] = Array.isArray(value) && value.length === 1 ? value[0] : value;
      }
    });

    if (requestData['product_price_type'] === undefined) {
      const productPriceType = this.resolveProductPriceType(filters, effectiveFilters);
      if (productPriceType !== undefined && productPriceType !== null && productPriceType !== '') {
        requestData['product_price_type'] = Array.isArray(productPriceType) && productPriceType.length === 1
          ? productPriceType[0]
          : productPriceType;
      }
    }

    return requestData;
  }

  private resolveProductPriceType(filters: any, effectiveFilters: any): any {
    const currentFilters = this.summaryGlobalFilterService.getCurrentFilters() || {};
    const candidateSources = [
      filters?.requestParams,
      filters?.queryParams,
      filters?.navigationRequestParams,
      effectiveFilters?.requestParams,
      effectiveFilters?.queryParams,
      effectiveFilters?.navigationRequestParams,
      currentFilters?.requestParams,
      currentFilters?.queryParams,
      currentFilters?.navigationRequestParams,
    ];

    for (const source of candidateSources) {
      const value = source?.product_price_type;
      if (value !== undefined && value !== null && value !== '') {
        return value;
      }
    }

    return undefined;
  }

  private resolveEffectiveFilters(filters: any): any {
    const currentFilters = this.summaryGlobalFilterService.getCurrentFilters() || {};
    return {
      ...currentFilters,
      ...filters,
      queryParams: {
        ...(currentFilters?.queryParams || {}),
        ...(filters?.queryParams || {}),
      },
      requestParams: {
        ...(currentFilters?.navigationRequestParams || {}),
        ...(currentFilters?.requestParams || {}),
        ...(filters?.navigationRequestParams || {}),
        ...(filters?.requestParams || {}),
      },
      navigationRequestParams: {
        ...(currentFilters?.navigationRequestParams || {}),
        ...(filters?.navigationRequestParams || {}),
      },
    };
  }

  private resolveSummaryUrl(url: string): string {
    const isLocalAssets = url.startsWith('assets') || url.startsWith('/assets');
    const isAbsoluteUrl = /^[a-z][a-z0-9+.-]*:\/\//i.test(url);
    if (isLocalAssets || isAbsoluteUrl) {
      return url;
    }

    const baseUrl = environment.URL.endsWith('/')
      ? environment.URL.slice(0, -1)
      : environment.URL;
    const path = url.startsWith('/') ? url : `/${url}`;
    return `${baseUrl}${path}`;
  }

  private resolveSummaryHeaders(filters: any): Record<string, string> {
    const headers = {
      ...(this.data?.headers || {}),
      ...(filters?.headers || {}),
      'X-TENANT-ID': this.data?.headers?.['X-TENANT-ID'] || filters?.headers?.['X-TENANT-ID'] || 'tenantA',
    };

    const token = this.keycloakService.getKeycloakInstance()?.token;
    const configuredAuthorization = headers['Authorization'] || headers['authorization'];
    if (!this.isBearerTokenEnabled()) {
      delete headers['Authorization'];
      delete headers['authorization'];
      return headers;
    }

    if (configuredAuthorization === QuadBubbleLegendComponent.BEARER_TOKEN_PLACEHOLDER && token) {
      headers['Authorization'] = `Bearer ${token}`;
      delete headers['authorization'];
    }

    return headers;
  }

  private isBearerTokenEnabled(): boolean {
    const flag = (environment as any).bearer_token;
    if (typeof flag === 'boolean') {
      return flag;
    }
    return String(flag ?? '').trim().toLowerCase() !== 'false';
  }

  private initializeSummaryLoader(): void {
    if (!this.data?.apiUrl) {
      return;
    }

    if (this.summaryWrapperAttached) {
      this.loadSummaryCards();
      return;
    }

    const initialFilters = this.summaryWrapper.attach(this.data, {
      onReload: (filters) => this.loadSummaryCards(filters),
      getChart: () => null,
      getSeriesNames: () => [],
      getCategories: () => [],
    });

    this.summaryWrapperAttached = true;
    this.loadSummaryCards(initialFilters || {});
  }

  private toCssSize(value: unknown): string | null {
    if (value === undefined || value === null || value === '') {
      return null;
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      return `${value}px`;
    }

    return String(value).trim() || null;
  }

  private buildSummaryCards(rows: any[]): SummaryCard[] {
    const groupByFields = this.getStringList(
      this.data?.summaryGroupByFields || this.data?.groupByFields,
      ['application', 'segment', 'Application', 'Segment', 'title', 'name', 'category', 'key', 'application_name'],
    );
    const metricConfigs = this.getSummaryMetricConfigs();

    const groupedRows = rows.reduce((groups: Map<string, any[]>, row: any) => {
      const title = this.normalizeApplicationTitle(
        this.readField(row, groupByFields),
      );
      if (!title) return groups;

      const group = groups.get(title) || [];
      group.push(row);
      groups.set(title, group);
      return groups;
    }, new Map<string, any[]>());

    const sortedGroups = this.sortSummaryGroups(Array.from(groupedRows.entries()), metricConfigs);

    return sortedGroups
      .map(([title, groupRows]) => ({
        title,
        metrics: metricConfigs
          .filter((metric) => this.isMetricVisibleForGroup(metric, title))
          .map((metric) => this.buildSummaryMetric(groupRows, metric, title)),
        rows: groupRows,
      }))
      .filter((card) => !this.shouldHideSummaryCard(card.rows, metricConfigs, card.title))
      .map(({ title, metrics }) => ({
        title,
        metrics,
      }));
  }

  private isMetricVisibleForGroup(metric: SummaryMetricConfig, groupTitle: string): boolean {
    if (!metric.visibleGroups?.length) {
      return true;
    }

    const normalizedGroupTitle = this.normalizeApplicationTitle(groupTitle).toLowerCase();
    return metric.visibleGroups.some((group) => this.normalizeApplicationTitle(group).toLowerCase() === normalizedGroupTitle);
  }

  private shouldHideSummaryCard(rows: any[], metricConfigs: SummaryMetricConfig[], groupTitle: string): boolean {
    const rules = Array.isArray(this.data?.hideSummaryCardsWhen)
      ? this.data.hideSummaryCardsWhen
      : this.data?.hideSummaryCardsWhen
        ? [this.data.hideSummaryCardsWhen]
        : [];

    if (!rules.length) {
      return false;
    }

    return rules.some((rule: any) => this.matchesSummaryCardHideRule(rows, metricConfigs, groupTitle, rule));
  }

  private matchesSummaryCardHideRule(
    rows: any[],
    metricConfigs: SummaryMetricConfig[],
    groupTitle: string,
    rule: any,
  ): boolean {
    if (!rule || typeof rule !== 'object' || Array.isArray(rule)) {
      return false;
    }

    const metricLabel = String(rule.metricLabel || rule.label || rule.metric || '').trim().toLowerCase();
    if (!metricLabel) {
      return false;
    }

    const metricConfig = metricConfigs.find((metric) => String(metric.label || '').trim().toLowerCase() === metricLabel);
    if (!metricConfig) {
      return false;
    }

    const fields = this.resolveMetricFields(metricConfig, groupTitle);
    const aggregation = this.normalizeAggregation(rule.aggregation || metricConfig.aggregation);
    const actualValue = this.aggregateField(rows, fields, aggregation);
    if (actualValue === null || actualValue === undefined) {
      return false;
    }

    const operator = String(rule.operator || rule.comparison || 'eq').trim().toLowerCase();
    const expectedValue = Number(rule.value);
    if (!Number.isFinite(expectedValue)) {
      return false;
    }

    switch (operator) {
      case 'eq':
      case '=':
      case '==':
        return actualValue === expectedValue;
      case 'neq':
      case '!=':
        return actualValue !== expectedValue;
      case 'gt':
      case '>':
        return actualValue > expectedValue;
      case 'gte':
      case '>=':
        return actualValue >= expectedValue;
      case 'lt':
      case '<':
        return actualValue < expectedValue;
      case 'lte':
      case '<=':
        return actualValue <= expectedValue;
      default:
        return actualValue === expectedValue;
    }
  }

  private sortSummaryGroups(
    groups: Array<[string, any[]]>,
    metricConfigs: SummaryMetricConfig[],
  ): Array<[string, any[]]> {
    const sortConfig = this.getSummarySortConfig();
    if (!sortConfig) {
      return groups;
    }

    const direction = sortConfig.direction === 'asc' ? 1 : -1;
    const nullsLast = sortConfig.nulls !== 'first';

    return [...groups].sort((left, right) => {
      const leftValue = this.resolveSummarySortValue(left[1], sortConfig, metricConfigs);
      const rightValue = this.resolveSummarySortValue(right[1], sortConfig, metricConfigs);

      const leftMissing = leftValue === null;
      const rightMissing = rightValue === null;

      if (leftMissing && rightMissing) {
        return left[0].localeCompare(right[0]);
      }

      if (leftMissing || rightMissing) {
        if (leftMissing && rightMissing) {
          return 0;
        }

        return leftMissing
          ? (nullsLast ? 1 : -1)
          : (nullsLast ? -1 : 1);
      }

      if (leftValue !== rightValue) {
        return (leftValue - rightValue) * direction;
      }

      return left[0].localeCompare(right[0]);
    });
  }

  private getSummarySortConfig(): SummarySortConfig | null {
    const config = this.data?.summarySort || this.data?.cardSort || this.data?.sortCardsBy;
    if (!config) {
      return null;
    }

    if (typeof config === 'string') {
      const metricLabel = config.trim();
      return metricLabel
        ? { metricLabel, direction: 'desc', nulls: 'last' }
        : null;
    }

    if (typeof config !== 'object' || Array.isArray(config)) {
      return null;
    }

    const metricLabel = String(config.metricLabel || config.label || config.metric || '').trim();
    const field = String(config.field || config.key || '').trim();
    if (!metricLabel && !field) {
      return null;
    }

    return {
      metricLabel: metricLabel || undefined,
      field: field || undefined,
      direction: String(config.direction || 'desc').trim().toLowerCase() === 'asc' ? 'asc' : 'desc',
      nulls: String(config.nulls || 'last').trim().toLowerCase() === 'first' ? 'first' : 'last',
    };
  }

  private resolveSummarySortValue(
    rows: any[],
    sortConfig: SummarySortConfig,
    metricConfigs: SummaryMetricConfig[],
  ): number | null {
    if (sortConfig.field) {
      return this.aggregateField(rows, [sortConfig.field], 'first');
    }

    const targetMetric = metricConfigs.find((metric) => (
      String(metric.label || '').trim().toLowerCase() === String(sortConfig.metricLabel || '').trim().toLowerCase()
    ));
    if (!targetMetric) {
      return null;
    }

    const fields = this.getStringList(targetMetric.fields ?? targetMetric.field, []);
    return this.aggregateField(rows, fields, this.normalizeAggregation(targetMetric.aggregation));
  }

  private getSummaryMetricConfigs(): SummaryMetricConfig[] {
    const metrics = Array.isArray(this.data?.summaryMetrics)
      ? this.data.summaryMetrics
      : Array.isArray(this.data?.cardMetrics)
        ? this.data.cardMetrics
        : null;

    if (metrics?.length) {
      return metrics
        .filter((metric: any) => metric?.label)
        .map((metric: any) => {
          const visibleGroups = this.getStringList(metric.visibleGroups ?? metric.showForGroups ?? metric.groupsAllowed, []);
          return {
            label: String(metric.label),
            labelByGroup: this.toStringMap(metric.labelByGroup || metric.labelByValue || metric.labelOverrides),
            fieldsByGroup: this.toStringListMap(metric.fieldsByGroup || metric.fieldByGroup),
            requestCardValueByGroup: this.toStringMap(metric.requestCardValueByGroup || metric.cardRequestValueByGroup || metric.requestValueByGroup),
            requestCardValue: String(metric.requestCardValue || metric.cardRequestValue || metric.requestValue || '').trim() || undefined,
            fields: this.getStringList(metric.fields ?? metric.field, []),
            aggregation: this.normalizeAggregation(metric.aggregation),
            format: this.normalizeMetricFormat(metric.format),
            hot: metric.hot === true,
            dividerAfter: metric.dividerAfter === true,
            visibleGroups: visibleGroups.length ? visibleGroups : undefined,
          };
        });
    }

    return [
      { label: 'Price increase', fields: ['price_increase', 'avg_price_increase'], aggregation: 'avg', format: 'percent', hot: true },
      { label: 'PPI', fields: ['ppi', 'PPI', 'producer_price_index'], aggregation: 'avg', format: 'percent', dividerAfter: true },
      { label: 'CAT price realization', fields: ['cat_price_realization', 'cat_price_realisation', 'cummins_price_realization', 'cummins_price_realisation'], aggregation: 'avg', format: 'percent' },
      { label: 'Competitor price realization', fields: ['competitor_price_realization', 'competitor_price_realisation', 'competition_price_realization', 'competition_price_info_from_scrapping'], aggregation: 'avg', format: 'percent' },
      { label: 'Market share', fields: ['companies_market_share_in_the_segment', 'companies_market_share_segment', 'cummins_market_share_in_that_segment_engine_model_and_region', 'cummins_market_share_segment_engine_model_region'], aggregation: 'avg', format: 'percent' },
      { label: 'CAGR', fields: ['cagr_volume', 'cagr'], aggregation: 'avg', format: 'percent' },
      { label: 'Sales', fields: ['net_sales', 'total_sales', 'sales'], aggregation: 'sum', format: 'currency' },
      { label: 'Margin', fields: ['margin_needed', 'marginNeeded', 'margin needed', 'margin', 'Margin', 'margin_percent', 'margin_percentage', 'margin_contribution'], aggregation: 'avg', format: 'percent' },
    ];
  }

  private buildSummaryMetric(rows: any[], config: SummaryMetricConfig, groupTitle = ''): SummaryMetric {
    const fields = this.resolveMetricFields(config, groupTitle);
    const aggregation = this.normalizeAggregation(config.aggregation);
    const format = this.normalizeMetricFormat(config.format);
    const value = format === 'text'
      ? this.formatText(this.firstField(rows, fields))
      : this.formatMetricValue(this.aggregateField(rows, fields, aggregation), format);

    return {
      label: this.resolveMetricLabel(config, groupTitle),
      sourceLabel: config.label,
      requestCardValue: this.resolveRequestCardValue(config, groupTitle),
      groupTitle,
      value,
      hot: config.hot,
      dividerAfter: config.dividerAfter,
    };
  }

  private resolveMetricLabel(config: SummaryMetricConfig, groupTitle: string): string {
    const normalizedGroupTitle = this.normalizeApplicationTitle(groupTitle).toLowerCase();
    const override = Object.entries(config.labelByGroup || {}).find(([key]) => (
      this.normalizeApplicationTitle(key).toLowerCase() === normalizedGroupTitle
    ));

    return override?.[1] || config.label;
  }

  private resolveMetricFields(config: SummaryMetricConfig, groupTitle: string): string[] {
    const normalizedGroupTitle = this.normalizeApplicationTitle(groupTitle).toLowerCase();
    const override = Object.entries(config.fieldsByGroup || {}).find(([key]) => (
      this.normalizeApplicationTitle(key).toLowerCase() === normalizedGroupTitle
    ));

    return override?.[1]?.length ? override[1] : this.getStringList(config.fields ?? config.field, []);
  }

  private resolveRequestCardValue(config: SummaryMetricConfig, groupTitle: string): string | undefined {
    const normalizedGroupTitle = this.normalizeApplicationTitle(groupTitle).toLowerCase();
    const override = Object.entries(config.requestCardValueByGroup || {}).find(([key]) => (
      this.normalizeApplicationTitle(key).toLowerCase() === normalizedGroupTitle
    ));

    return override?.[1] || config.requestCardValue;
  }

  private resolveSummaryRows(source: any): any[] {
    const parsedSource = this.parsePossibleJson(source);
    source = parsedSource;

    const responsePath = String(this.data?.responsePath || '').trim();
    const resolved = responsePath ? this.readPath(source, responsePath) : null;
    const resolvedRows = this.normalizeSummaryRows(resolved);
    if (resolvedRows.length) {
      return this.enrichSummaryRowsFromDetailRows(resolvedRows, source);
    }

    if (responsePath && this.shouldUseOnlyConfiguredSummaryRows()) {
      return [];
    }

    if (this.isSummaryRowArray(source?.responseList?.[0]?.key_cons)) {
      return this.enrichSummaryRowsFromDetailRows(source.responseList[0].key_cons, source);
    }

    if (this.isSummaryRowArray(source)) {
      return source;
    }

    if (this.isSummaryRowArray(source?.responseList?.[0]?.data)) {
      return source.responseList[0].data;
    }

    if (this.isSummaryRowArray(source?.data)) {
      return source.data;
    }

    if (this.isSummaryRowArray(source?.key_cons)) {
      return source.key_cons;
    }

    const discoveredRows = this.findSummaryRowArray(source);
    if (discoveredRows.length) {
      return discoveredRows;
    }

    return source && typeof source === 'object' ? [source] : [];
  }

  private normalizeSummaryRows(value: any): any[] {
    value = this.parsePossibleJson(value);

    if (this.isSummaryRowArray(value)) {
      return value;
    }

    if (Array.isArray(value)) {
      return value.filter((item) => item && typeof item === 'object');
    }

    if (!value || typeof value !== 'object') {
      return [];
    }

    if (this.isSummaryRowArray(value.data)) {
      return value.data;
    }

    if (this.isSummaryRowArray(value.key_cons)) {
      return value.key_cons;
    }

    const entries = Object.entries(value);
    if (!entries.length) {
      return [];
    }

    return entries
      .map(([key, item]) => {
        const parsedItem = this.parsePossibleJson(item);
        if (!parsedItem || typeof parsedItem !== 'object' || Array.isArray(parsedItem)) {
          return null;
        }

        return {
          application: this.readField(parsedItem, ['application', 'segment', 'title', 'name']) ?? key,
          ...parsedItem,
        };
      })
      .filter((item): item is any => !!item);
  }

  private enrichSummaryRowsFromDetailRows(summaryRows: any[], source: any): any[] {
    if (this.shouldUseOnlyConfiguredSummaryRows() || this.data?.enrichSummaryFromDetailRows === false) {
      return summaryRows;
    }

    const detailRowsPath = String(
      this.data?.summaryDetailRowsPath ||
      this.data?.detailRowsPath ||
      this.data?.detailResponsePath ||
      '',
    ).trim();
    const configuredDetailRows = detailRowsPath ? this.readPath(source, detailRowsPath) : null;
    const detailRows = this.normalizeSummaryRows(configuredDetailRows || source?.responseList?.[0]?.data || source?.data);
    if (!summaryRows.length || !detailRows.length) {
      return summaryRows;
    }

    const groupByFields = this.getStringList(
      this.data?.summaryGroupByFields || this.data?.groupByFields,
      ['application', 'segment', 'Application', 'Segment', 'title', 'name', 'category', 'key', 'application_name'],
    );
    const metricConfigs = this.getSummaryMetricConfigs();

    return summaryRows.map((summaryRow) => {
      const summaryTitle = this.normalizeApplicationTitle(this.readField(summaryRow, groupByFields));
      if (!summaryTitle) {
        return summaryRow;
      }

      const matchingDetails = detailRows.filter((detailRow) => (
        this.normalizeApplicationTitle(this.readField(detailRow, groupByFields)) === summaryTitle
      ));

      if (!matchingDetails.length) {
        return summaryRow;
      }

      const enrichedRow = { ...summaryRow };
      metricConfigs.forEach((metric) => {
        const fields = this.getStringList(metric.fields ?? metric.field, []);
        if (!fields.length || this.firstField([enrichedRow], fields) !== null) {
          return;
        }

        const value = this.aggregateField(matchingDetails, fields, this.normalizeAggregation(metric.aggregation));
        if (value !== null) {
          enrichedRow[fields[0]] = value;
        }
      });

      return enrichedRow;
    });
  }

  private shouldUseOnlyConfiguredSummaryRows(): boolean {
    return this.data?.summaryUseOnlyResponsePath === true || this.data?.strictResponsePath === true;
  }

  private readPath(source: any, path: string): any {
    return path.split('.').reduce((current: any, segment: string) => current?.[segment], source);
  }

  private findSummaryRowArray(source: any): any[] {
    source = this.parsePossibleJson(source);

    if (!source || typeof source !== 'object') {
      return [];
    }

    if (this.isSummaryRowArray(source)) {
      return source;
    }

    const values = Array.isArray(source) ? source : Object.values(source);
    for (const value of values) {
      const rows = this.findSummaryRowArray(value);
      if (rows.length) {
        return rows;
      }
    }

    return [];
  }

  private isSummaryRowArray(value: any): value is any[] {
    return Array.isArray(value) && value.some((item) => (
      item &&
      typeof item === 'object' &&
      (
        this.readField(item, ['application', 'segment', 'Application', 'Segment']) !== undefined ||
        this.readField(item, ['net_sales', 'sales', 'total_sales', 'Net Sales']) !== undefined ||
        this.readField(item, ['margin_needed', 'margin', 'Margin', 'margin_contribution']) !== undefined ||
        this.readField(item, ['customer_name', 'Customer Name']) !== undefined
      )
    ));
  }

  private readField(row: any, fieldNames: string[]): any {
    if (!row || typeof row !== 'object') {
      return undefined;
    }

    for (const fieldName of fieldNames) {
      if (fieldName.includes('.')) {
        const value = this.readPath(row, fieldName);
        if (value !== undefined) {
          return value;
        }
      }

      if (row[fieldName] !== undefined) {
        return row[fieldName];
      }
    }

    const lowerCaseMap = Object.keys(row).reduce<Record<string, string>>((acc, key) => {
      acc[key.toLowerCase()] = key;
      return acc;
    }, {});
    const normalizedKeyMap = Object.keys(row).reduce<Record<string, string>>((acc, key) => {
      acc[this.normalizeFieldName(key)] = key;
      return acc;
    }, {});

    for (const fieldName of fieldNames) {
      const actualKey = lowerCaseMap[fieldName.toLowerCase()];
      if (actualKey && row[actualKey] !== undefined) {
        return row[actualKey];
      }

      const normalizedActualKey = normalizedKeyMap[this.normalizeFieldName(fieldName)];
      if (normalizedActualKey && row[normalizedActualKey] !== undefined) {
        return row[normalizedActualKey];
      }
    }

    return undefined;
  }

  private normalizeFieldName(value: string): string {
    return String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  private parsePossibleJson(value: any): any {
    if (typeof value !== 'string') {
      return value;
    }

    const trimmed = value.trim();
    if (!trimmed || (!trimmed.startsWith('{') && !trimmed.startsWith('['))) {
      return value;
    }

    try {
      return JSON.parse(trimmed);
    } catch {
      return value;
    }
  }

  private normalizeApplicationTitle(application: any): string {
    return String(application || '').trim().toUpperCase();
  }

  private averageField(rows: any[], fieldNames: string[]): number | null {
    const values = this.collectNumbers(rows, fieldNames);
    if (!values.length) return null;
    return values.reduce((sum, value) => sum + value, 0) / values.length;
  }

  private sumField(rows: any[], fieldNames: string[]): number | null {
    const values = this.collectNumbers(rows, fieldNames);
    if (!values.length) return null;
    return values.reduce((sum, value) => sum + value, 0);
  }

  private aggregateField(rows: any[], fieldNames: string[], aggregation: 'avg' | 'sum' | 'first'): number | null {
    if (aggregation === 'sum') {
      return this.sumField(rows, fieldNames);
    }

    if (aggregation === 'first') {
      return this.firstNumber(rows.find((row) => this.firstNumber(row, fieldNames) !== null), fieldNames);
    }

    return this.averageField(rows, fieldNames);
  }

  private firstField(rows: any[], fieldNames: string[]): any {
    for (const row of rows) {
      const value = this.readField(row, fieldNames);
      if (value !== undefined && value !== null && value !== '') {
        return value;
      }
    }

    return null;
  }

  private collectNumbers(rows: any[], fieldNames: string[]): number[] {
    return rows
      .map((row) => this.firstNumber(row, fieldNames))
      .filter((value): value is number => value !== null);
  }

  private firstNumber(row: any, fieldNames: string[]): number | null {
    if (!row) return null;

    for (const fieldName of fieldNames) {
      const rawValue = this.readField(row, [fieldName]);
      if (rawValue === null || rawValue === undefined || rawValue === '') continue;

      const numericValue = this.parseNumericValue(rawValue);
      if (numericValue !== null) {
        return numericValue;
      }
    }

    return null;
  }

  private parseNumericValue(rawValue: any): number | null {
    if (rawValue === null || rawValue === undefined || rawValue === '') {
      return null;
    }

    if (typeof rawValue === 'number') {
      return Number.isFinite(rawValue) ? rawValue : null;
    }

    const value = String(rawValue).trim();
    if (!value) {
      return null;
    }

    const negativeViaParens = value.startsWith('(') && value.endsWith(')');
    const normalized = value
      .replace(/[,$%\s]/g, '')
      .replace(/[()]/g, '')
      .replace(/,/g, '');
    const suffix = normalized.slice(-1).toUpperCase();
    const multiplier = suffix === 'K'
      ? 1_000
      : suffix === 'M'
        ? 1_000_000
        : suffix === 'B'
          ? 1_000_000_000
          : 1;
    const numericPortion = multiplier === 1 ? normalized : normalized.slice(0, -1);
    const parsed = Number(numericPortion);

    if (!Number.isFinite(parsed)) {
      return null;
    }

    const signedValue = negativeViaParens ? -parsed : parsed;
    return signedValue * multiplier;
  }

  private formatMetricValue(value: number | null, format: 'percent' | 'currency' | 'number' | 'text'): string {
    if (format === 'currency') {
      return this.formatCurrencyCompact(value);
    }

    if (format === 'number') {
      return value === null ? '' : this.formatSummaryNumber(value);
    }

    return this.formatPercentOrNA(value);
  }

  private formatText(value: any): string {
    return value === null || value === undefined ? '' : String(value);
  }

  private normalizeAggregation(value: any): 'avg' | 'sum' | 'first' {
    const normalized = String(value || 'avg').trim().toLowerCase();
    return normalized === 'sum' || normalized === 'first' ? normalized : 'avg';
  }

  private normalizeMetricFormat(value: any): 'percent' | 'currency' | 'number' | 'text' {
    const normalized = String(value || 'percent').trim().toLowerCase();
    if (normalized === 'currency' || normalized === 'number' || normalized === 'text') {
      return normalized;
    }

    return 'percent';
  }

  private getStringList(value: any, fallback: string[]): string[] {
    if (Array.isArray(value)) {
      const values = value.map((item) => String(item || '').trim()).filter(Boolean);
      return values.length ? values : fallback;
    }

    if (typeof value === 'string' && value.trim()) {
      return value.split(',').map((item) => item.trim()).filter(Boolean);
    }

    return fallback;
  }

  private toStringMap(value: any): Record<string, string> {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return {};
    }

    return Object.fromEntries(
      Object.entries(value)
        .map(([key, label]) => [String(key || '').trim(), String(label || '').trim()])
        .filter(([key, label]) => Boolean(key && label)),
    );
  }

  private toStringListMap(value: any): Record<string, string[]> {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return {};
    }

    return Object.fromEntries(
      Object.entries(value)
        .map(([key, fields]) => [String(key || '').trim(), this.getStringList(fields, [])])
        .filter(([key, fields]) => Boolean(key && fields.length)),
    );
  }

  private formatPercent(value: number | null): string {
    return value === null ? '' : `${this.formatSummaryNumber(value)}%`;
  }

  private formatPercentOrNA(value: number | null): string {
    return value === null ? '' : `${this.formatSummaryNumber(value)}%`;
  }

  private formatCurrencyCompact(value: number | null): string {
    if (value === null) return '';

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(value);
  }

  private formatSummaryNumber(value: number): string {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  }

  private getSummaryLoadSignature(filters: any): string {
    return JSON.stringify({
      requestParams: filters?.requestParams || {},
      queryParams: filters?.queryParams || {},
      widgetId: filters?.widgetId || null,
      changedFilterKey: filters?.changedFilterKey || null,
      filterListener: filters?.filterListener || filters?.filterListner || null,
    });
  }

  getMetricHoverConfig(metric: SummaryMetric): any {
    if (!this.hoverPopupsEnabled) {
      return null;
    }

    const hoverCharts = this.data?.hoverCharts || {};
    const sourceLabel = String(metric?.sourceLabel || '').trim();
    const displayLabel = String(metric?.label || '').trim();
    const sourceKey = sourceLabel.toLowerCase();
    const displayKey = displayLabel.toLowerCase();

    return hoverCharts[sourceLabel] ||
      hoverCharts[displayLabel] ||
      hoverCharts[sourceKey] ||
      hoverCharts[displayKey] ||
      null;
  }

  private loadMetricHoverChart(metric: SummaryMetric, chartConfig: any): void {
    const payload = this.buildMetricHoverPayload(metric, chartConfig);
    const url = this.resolveSummaryUrl(chartConfig.apiUrl || this.data?.apiUrl || '');
    if (!url) {
      this.hoverChartLoading = false;
      this.hoverChartError = 'Hover chart not configured.';
      return;
    }

    this.hoverDataSub?.unsubscribe();
    this.hoverDataSub = this.summaryHttp.post(url, payload, {
      headers: this.resolveSummaryHeaders({ headers: chartConfig.headers }),
    }).subscribe({
      next: (response) => {
        if (this.activeHoverMetric !== metric) {
          return;
        }

        const lineData = this.buildMetricHoverLineData(response, chartConfig);
        this.hoverChartLoading = false;
        this.hoverChartError = lineData.category.length ? '' : 'Data unavailable';
        this.activeHoverConfig = {
          ...this.activeHoverConfig,
          title: chartConfig.title || metric.label,
          data: lineData,
          height: chartConfig.height || '210px',
        };
      },
      error: () => {
        if (this.activeHoverMetric !== metric) {
          return;
        }

        this.hoverChartLoading = false;
        this.hoverChartError = 'Data unavailable';
      },
    });
  }

  private buildMetricHoverPayload(metric: SummaryMetric, chartConfig: any): Record<string, any> {
    const effectiveFilters = this.resolveEffectiveFilters({});
    const productPriceType = this.resolveProductPriceType({}, effectiveFilters);
    const requestParams = {
      ...(effectiveFilters?.navigationRequestParams || {}),
      ...(effectiveFilters?.requestParams || {}),
    };
    const queryParams = effectiveFilters?.queryParams || {};
    const cardsValue = chartConfig.cardsValue || chartConfig.cardValue || metric.requestCardValue || metric.sourceLabel;
    const requestData: Record<string, any> = {
      ...(chartConfig.requestData || {}),
      cards: cardsValue,
    };
    const payloadKeys = this.getStringList(
      chartConfig?.requestPayloadKeys || chartConfig?.requestFilterKeys,
      [],
    );
    const keyMap = chartConfig?.requestKeyMap || this.data?.requestKeyMap || {};

    payloadKeys.forEach((key) => {
      const requestKey = keyMap?.[key] || key;
      const value = requestParams[key] ?? queryParams[key] ?? requestParams[requestKey] ?? queryParams[requestKey];
      if (value !== undefined && value !== null && value !== '') {
        requestData[requestKey] = Array.isArray(value) && value.length === 1 ? value[0] : value;
      }
    });

    if ((requestData['application'] === undefined || requestData['application'] === null || requestData['application'] === '') && metric?.groupTitle) {
      requestData['application'] = metric.groupTitle;
    }

    if (productPriceType !== undefined && productPriceType !== null && productPriceType !== '') {
      requestData['product_price_type'] = Array.isArray(productPriceType) && productPriceType.length === 1
        ? productPriceType[0]
        : productPriceType;
    }

    return {
      requestData,
      responseList: [],
      entity: chartConfig.entity || this.data?.entity || 'default_entity',
      workflowId: chartConfig.workflowId || this.data?.workflowId || 'default_workflow',
      endpoints: [
        {
          functionName: chartConfig.functionName || 'Card_Popup_Fetch_Service',
          version: chartConfig.version || this.data?.version || '1.0',
        },
      ],
      communicationType: 'rest',
      version: chartConfig.version || this.data?.version || '1.0',
    };
  }

  private buildMetricHoverLineData(response: any, chartConfig: any): { category: string[]; value: Array<number | null> } {
    const responsePath = String(chartConfig.responsePath || '').trim();
    const root = responsePath ? this.readPath(response, responsePath) : response;
    const dataKey = String(chartConfig.dataKey || '').trim();
    const source = dataKey ? this.readPath(root, dataKey) : root;
    const yearSeries = this.buildYearSeriesFromRow(source, chartConfig);
    if (yearSeries) {
      return yearSeries;
    }

    const rows = this.parseHoverRows(source);
    const categoryKey = chartConfig.categoryKey || 'category';
    const valueKey = chartConfig.valueKey || 'value';
    const points = rows
      .map((row: any, index: number) => ({
        category: String(row?.[categoryKey] ?? row?.category ?? index + 1),
        value: this.parseNumericValue(row?.[valueKey] ?? row?.value),
      }))
      .filter((point: { category: string; value: number | null }) => point.value !== null);

    return {
      category: points.map((point) => point.category),
      value: points.map((point) => point.value as number),
    };
  }

  private buildYearSeriesFromRow(source: any, chartConfig: any): { category: string[]; value: Array<number | null> } | null {
    const row = Array.isArray(source) ? source[0] : source;
    if (!row || typeof row !== 'object') {
      return null;
    }

    const configuredYears = Array.isArray(chartConfig?.years)
      ? chartConfig.years
      : [2022, 2023, 2024, 2025, 2026];
    const yearPrefix = String(chartConfig?.yearKeyPrefix || 'year_');
    const points = configuredYears
      .map((year: number | string) => ({
        category: String(year),
        value: this.parseNumericValue(row?.[`${yearPrefix}${year}`]),
      }));

    if (!points.length) {
      return null;
    }

    return {
      category: points.map((point: { category: string; value: number | null }) => point.category),
      value: points.map((point: { category: string; value: number | null }) => point.value),
    };
  }

  private parseHoverRows(value: any): any[] {
    value = this.parsePossibleJson(value);

    if (Array.isArray(value)) {
      return value.filter((item) => item && typeof item === 'object');
    }

    if (!value || typeof value !== 'object') {
      return [];
    }

    const preferredKeys = ['line', 'data', 'series', 'values', 'points'];
    for (const key of preferredKeys) {
      if (Array.isArray(value?.[key])) {
        return value[key].filter((item: any) => item && typeof item === 'object');
      }
    }

    const nestedArray = Object.values(value).find((item) => Array.isArray(item));
    return Array.isArray(nestedArray)
      ? nestedArray.filter((item) => item && typeof item === 'object')
      : [];
  }

  private hideHoverCard(): void {
    this.clearHideHoverTimer();
    this.hoverDataSub?.unsubscribe();
    this.hoverDataSub = undefined;
    this.activeHoverMetric = null;
    this.activeHoverConfig = null;
    this.activeHoverStyle = null;
    this.activeHoverAnchorRect = null;
    this.hoverChartLoading = false;
    this.hoverChartError = '';
  }

  private clearHideHoverTimer(): void {
    if (!this.hideHoverTimer) {
      return;
    }

    clearTimeout(this.hideHoverTimer);
    this.hideHoverTimer = undefined;
  }

  private getHoverAnchorRect(event: MouseEvent): { left: number; right: number; top: number; width: number; height: number } | null {
    const element = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
    const rect = element?.getBoundingClientRect();

    return rect ? {
      left: rect.left,
      right: rect.right,
      top: rect.top,
      width: rect.width,
      height: rect.height,
    } : null;
  }

  private buildHoverStyle(chartConfig: any, anchorRect: { left: number; right: number; top: number; width: number; height: number } | null): Record<string, string> {
    const viewportWidth = window.innerWidth || 1440;
    const viewportHeight = window.innerHeight || 900;
    const margin = 16;
    const width = Math.min(Number(chartConfig?.popupWidth ?? 420), viewportWidth - (margin * 2));
    const height = Math.min(Number(chartConfig?.popupHeight ?? 260), viewportHeight - (margin * 2));
    const gap = Number(chartConfig?.popupGap ?? 8);
    const offsetX = Number(chartConfig?.popupOffsetX ?? 0);
    const offsetY = Number(chartConfig?.popupOffsetY ?? 0);
    const placement = String(chartConfig?.popupPlacement || 'auto').trim().toLowerCase();

    if (!anchorRect) {
      return {
        width: `${width}px`,
        height: 'auto',
        'max-height': `${height}px`,
        left: `${Math.max(margin, Math.round((viewportWidth - width) / 2))}px`,
        top: `${Math.max(margin, Math.round((viewportHeight - height) / 2))}px`,
      };
    }

    const spaceOnLeft = anchorRect.left - margin;
    const spaceOnRight = viewportWidth - anchorRect.right - margin;
    const centeredLeft = Math.max(
      margin,
      Math.min(anchorRect.left + (anchorRect.width / 2) - (width / 2), viewportWidth - width - margin),
    );
    const left = placement === 'right'
      ? (spaceOnRight >= width
          ? anchorRect.right + gap
          : (spaceOnLeft >= width ? anchorRect.left - width - gap : centeredLeft))
      : placement === 'left'
        ? (spaceOnLeft >= width
            ? anchorRect.left - width - gap
            : (spaceOnRight >= width ? anchorRect.right + gap : centeredLeft))
        : centeredLeft;
    const centeredTop = Math.max(
      margin,
      Math.min(anchorRect.top + (anchorRect.height / 2) - (height / 2), viewportHeight - height - margin),
    );
    const preferredTop = anchorRect.top - height - gap;
    const belowTop = anchorRect.top + anchorRect.height + gap;
    const top = placement === 'below'
      ? Math.min(belowTop, viewportHeight - height - margin)
      : placement === 'above'
        ? Math.max(margin, preferredTop)
        : placement === 'left' || placement === 'right'
          ? centeredTop
          : (preferredTop >= margin ? preferredTop : Math.min(belowTop, viewportHeight - height - margin));

    return {
      width: `${Math.round(width)}px`,
      height: 'auto',
      'max-height': `${Math.round(height)}px`,
      left: `${Math.round(left + offsetX)}px`,
      top: `${Math.round(top + offsetY)}px`,
      ...(chartConfig?.popupPadding ? { padding: String(chartConfig.popupPadding) } : {}),
      ...(chartConfig?.popupBorderRadius ? { 'border-radius': String(chartConfig.popupBorderRadius) } : {}),
      ...(chartConfig?.popupBoxShadow ? { 'box-shadow': String(chartConfig.popupBoxShadow) } : {}),
    };
  }

  private normalizeHoverKey(value: any): string {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'metric';
  }
}
